import React from "react";
import { Languages } from "lucide-react";
import { useT } from "../../context/LanguageContext";

/**
 * Compact EN/ES segmented switch. Used in the Navbar (desktop + mobile menu)
 * and in the Settings → Preferences card. Persists via the LanguageProvider
 * (localStorage `tasklio_lang`). Purely a UI-language control — does NOT
 * affect the per-lead AI Reply / Quote language selectors in LeadDetail.
 *
 * Variants:
 *   - "compact" (default): icon + 2 small pills, ideal for navbars.
 *   - "wide": full-width pills with labels, ideal for Settings cards.
 */

// Pill is hoisted out of LanguageSwitcher so React doesn't re-create the
// component type on every render (which would unmount/remount the
// underlying <button>, breaking focus and any in-flight animations).
const Pill = ({ active, value, onClick, testid, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={
      "px-2.5 py-1 rounded-md text-xs font-semibold transition-colors " +
      (active
        ? "bg-white text-green-800 shadow-sm"
        : "text-gray-500 hover:text-gray-800")
    }
    data-testid={`${testid}-${value}`}
    aria-pressed={active}
  >
    {children}
  </button>
);

export const LanguageSwitcher = ({ variant = "compact", testidPrefix = "lang-switch" }) => {
  const { lang, setLang, t } = useT();

  if (variant === "wide") {
    return (
      <div
        className="inline-flex items-center gap-1 bg-gray-100 border border-gray-200 rounded-lg p-1"
        data-testid={testidPrefix}
      >
        <button
          type="button"
          onClick={() => setLang("en")}
          className={
            "px-4 py-2 rounded-md text-sm font-semibold transition-colors " +
            (lang === "en" ? "bg-white text-green-800 shadow-sm" : "text-gray-600 hover:text-gray-900")
          }
          data-testid={`${testidPrefix}-en`}
          aria-pressed={lang === "en"}
        >
          {t("common.english")}
        </button>
        <button
          type="button"
          onClick={() => setLang("es")}
          className={
            "px-4 py-2 rounded-md text-sm font-semibold transition-colors " +
            (lang === "es" ? "bg-white text-green-800 shadow-sm" : "text-gray-600 hover:text-gray-900")
          }
          data-testid={`${testidPrefix}-es`}
          aria-pressed={lang === "es"}
        >
          {t("common.spanish")}
        </button>
      </div>
    );
  }

  return (
    <div
      className="inline-flex items-center gap-1 bg-gray-100 border border-gray-200 rounded-lg p-0.5"
      data-testid={testidPrefix}
      title={t("lang.appLanguage")}
    >
      <Languages className="w-3.5 h-3.5 text-gray-400 ml-1.5" aria-hidden="true" />
      <Pill
        active={lang === "en"}
        value="en"
        onClick={() => setLang("en")}
        testid={testidPrefix}
      >
        <span className="mr-1" aria-hidden="true">🇺🇸</span>EN
      </Pill>
      <Pill
        active={lang === "es"}
        value="es"
        onClick={() => setLang("es")}
        testid={testidPrefix}
      >
        <span className="mr-1" aria-hidden="true">🇪🇸</span>ES
      </Pill>
    </div>
  );
};
